import React, { useEffect, useMemo, useState } from 'react';
import Wrapper from '../../components/Wrapper';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Alert,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { shortFunction, menu } from '../../translate';
import { greenTheme, employeePermissionsStyles as eps } from '../../helpers/style';
import Api from '../../Api';

const { REACT_APP_SERVER } = process.env;
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary;
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover;
import { toast } from 'react-toastify';

export const allPermissions = [
  { id: 'employees_read', name: ['', 'Employees: view', 'Сотрудники: просмотр', 'Աշխատակիցներ․ դիտում'] },
  { id: 'employees_create', name: ['', 'Employees: create', 'Сотрудники: создание', 'Աշխատակիցներ․ ստեղծում'] },
  { id: 'employees_edit', name: ['', 'Employees: edit', 'Сотрудники: редактирование', 'Աշխատակիցներ․ խմբագրում'] },

  { id: 'groups_read', name: ['', 'Groups: view', 'Группы: просмотр', 'Խմբեր․ դիտում'] },
  { id: 'groups_create', name: ['', 'Groups: create', 'Группы: создание', 'Խմբեր․ ստեղծում'] },
  { id: 'groups_edit', name: ['', 'Groups: edit', 'Группы: редактирование', 'Խմբեր․ խմբագրում'] },

  { id: 'ivr_read', name: ['', 'Voice menu: view', 'Голосовое меню: просмотр', 'Ձայնային մենյու․ դիտում'] },
  { id: 'ivr_create', name: ['', 'Voice menu: create', 'Голосовое меню: создание', 'Ձայնային մենյու․ ստեղծում'] },
  { id: 'ivr_edit', name: ['', 'Voice menu: edit', 'Голосовое меню: редактирование', 'Ձայնային մենյու․ խմբագրում'] },

  { id: 'did_read', name: ['', 'Main number: view', 'Основной номер: просмотр', 'Գլխավոր համար․ դիտում'] },
  { id: 'did_create', name: ['', 'Main number: create', 'Основной номер: создание', 'Գլխավոր համար․ ստեղծում'] },

  { id: 'online_view', name: ['', 'Online call viewing', 'Просмотр онлайн-звонков', 'Արցանց զանգերի դիտում'] },
  { id: 'history_view', name: ['', 'View call history', 'Просмотр истории звонков', 'Զանգերի պատմության դիտում'] },
  { id: 'call_history_own', name: ['', 'Call history: own', 'История звонков: своя', 'Զանգերի պատմություն․ սեփական'] },
  { id: 'call_history_all', name: ['', 'Call history: all', 'История звонков: вся', 'Զանգերի պատմություն․ ամբողջը'] },

  { id: 'recordings_listen_own', name: ['', 'Recordings: listen own', 'Записи: слушать свои', 'Ձայնագրություններ․ լսել սեփական'] },
  { id: 'recordings_listen_all', name: ['', 'Recordings: listen all', 'Записи: слушать все', 'Ձայնագրություններ․ լսել բոլորը'] },

  { id: 'group_history_view', name: ['', 'View group history', 'Просмотр истории группы', 'Խմբի պատմության դիտում'] },
  { id: 'group_audio_view', name: ['', 'View group audio', 'Просмотр аудио группы', 'Խմբի ձայնագրությունների դիտում'] },

  { id: 'report_view_own', name: ['', 'Report: view own', 'Отчёт: свой', 'Հաշվետվություն․ սեփական'] },
  { id: 'report_view_all', name: ['', 'Report: view all', 'Отчёт: все', 'Հաշվետվություն․ բոլորը'] },
];

export const defaultRoles = {
  admin: {
    id: 'admin',
    name: ['', 'Administrator', 'Администратор', 'Ադմինիստրատոր'],
    color: '#7ebb08',
    isDefault: true,
    permissions: allPermissions.map((p) => p.id),
  },
  manager: {
    id: 'manager',
    name: ['', 'Manager', 'Менеджер', 'Մենեջեր'],
    color: '#0b73b9',
    isDefault: true,
    permissions: [
      'employees_read', 'employees_create', 'employees_edit',
      'groups_read', 'groups_create', 'groups_edit',
      'ivr_read', 'ivr_create', 'ivr_edit',
      'did_read', 'did_create',
      'online_view', 'history_view', 'call_history_all', 'recordings_listen_all', 'report_view_all',
    ],
  },
  employee: {
    id: 'employee',
    name: ['', 'Employee', 'Сотрудник', 'Աշխատակից'],
    color: '#9e9e9e',
    isDefault: true,
    permissions: ['history_view', 'call_history_own', 'recordings_listen_own', 'report_view_own'],
  },
};

const GROUP_HISTORY_PREFIX = 'gh:';

export function splitStoredPermissions(raw) {
  let arr = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) arr = parsed;
    } catch (e) {
      arr = [];
    }
  }
  const permissions = [];
  const groupHistoryIds = [];
  for (const item of arr) {
    if (typeof item !== 'string') continue;
    if (item.startsWith(GROUP_HISTORY_PREFIX)) {
      const id = Number(item.slice(GROUP_HISTORY_PREFIX.length));
      if (Number.isFinite(id) && id > 0) groupHistoryIds.push(id);
    } else {
      permissions.push(item);
    }
  }
  return {
    permissions: [...new Set(permissions)],
    groupHistoryIds: [...new Set(groupHistoryIds)],
  };
}

export function packPermissions(permissions, groupHistoryIds) {
  const perms = [...new Set((permissions || []).filter((item) => typeof item === 'string' && !item.startsWith(GROUP_HISTORY_PREFIX)))];
  const ids = [...new Set((groupHistoryIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  return [...perms, ...ids.map((id) => `${GROUP_HISTORY_PREFIX}${id}`)];
}

export function apiRolesToUiRoles(list) {
  if (!Array.isArray(list) || list.length === 0) return {};
  const obj = {};
  for (const row of list) {
    const stored = splitStoredPermissions(row.permissions_json);
    obj[row.role_key] = {
      id: row.role_key,
      dbId: row.id,
      name: ['', row.name_en || '', row.name_ru || '', row.name_hy || ''],
      color: row.color || '#7ebb08',
      isDefault: !!row.is_default,
      permissions: stored.permissions,
      groupHistoryIds: stored.groupHistoryIds,
    };
  }
  return obj;
}

function EmployeePermissions() {
  const navigate = useNavigate();
  const langIndex = +localStorage.getItem('atsLang') || 1;

  const [roles, setRoles] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);

  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [editingRoleKey, setEditingRoleKey] = useState(null);
  const [newRoleName, setNewRoleName] = useState({ en: '', ru: '', hy: '' });
  const [newRoleColor, setNewRoleColor] = useState('#7ebb08');
  const [savingNewRole, setSavingNewRole] = useState(false);

  const [lastLoadedRoles, setLastLoadedRoles] = useState(null);
  const [deletingRoleKey, setDeletingRoleKey] = useState(null);

  const normalizeRoleKey = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');

  useEffect(() => {
    (async () => {
      try {
        const resp = await Api.rolesList()
        const list = resp?.data?.data;

        if (Array.isArray(list) && list.length) {
          const uiRoles = apiRolesToUiRoles(list);
          setRoles(uiRoles);
          setLastLoadedRoles(uiRoles);
        } else {
          setRoles(defaultRoles);
          setLastLoadedRoles(defaultRoles);
        }
      } catch (e) {
        setRoles(defaultRoles);
        setLastLoadedRoles(defaultRoles);
      } finally {
        setLoadingRoles(false);
      }
    })();
  }, []);

  const rolesArray = useMemo(() => Object.values(roles), [roles]);

  const handleOpenRoleDialog = (role = null) => {
    if (role) {
      setEditingRoleKey(role.id);
      setNewRoleName({
        en: role.name?.[1] || '',
        ru: role.name?.[2] || '',
        hy: role.name?.[3] || '',
      });
      setNewRoleColor(role.color || '#7ebb08');
    } else {
      setEditingRoleKey(null);
      setNewRoleName({ en: '', ru: '', hy: '' });
      setNewRoleColor('#7ebb08');
    }
    setOpenRoleDialog(true);
  };

  const handleCloseRoleDialog = () => {
    setOpenRoleDialog(false);
    setEditingRoleKey(null);
    setNewRoleName({ en: '', ru: '', hy: '' });
    setNewRoleColor('#7ebb08');
  };

  const handleSaveRoleLocal = async () => {
    if (!newRoleName.en.trim()) {
      toast.error(
        langIndex === 1 ? 'Role name is required' :
        langIndex === 2 ? 'Название роли обязательно' :
        'Դերի անունը պարտադիր է'
      );
      return;
    }

    const roleKey = editingRoleKey || normalizeRoleKey(newRoleName.en) || `custom_${Date.now()}`;
    const newRole = {
      id: roleKey,
      name: ['', newRoleName.en, newRoleName.ru, newRoleName.hy],
      color: newRoleColor,
      isDefault: false,
      permissions: roles[roleKey]?.permissions || [],
      groupHistoryIds: roles[roleKey]?.groupHistoryIds || [],
    };
    const rolesWithNew = { ...roles, [roleKey]: newRole };

    setSavingNewRole(true);
    try {
      const payload = {
        roles: Object.values(rolesWithNew).map((r) => ({
          role_key: r.id,
          name_en: r.name?.[1] || '',
          name_ru: r.name?.[2] || '',
          name_hy: r.name?.[3] || '',
          color: r.color || null,
          is_default: r.isDefault ? 1 : 0,
          permissions: packPermissions(r.permissions, r.groupHistoryIds),
        })),
      };
      const resp = await Api.rolesSaveAll(payload);
      const list = resp?.data?.data;
      if (Array.isArray(list)) {
        const uiRoles = apiRolesToUiRoles(list);
        setRoles(uiRoles);
        setLastLoadedRoles(uiRoles);
      } else {
        setRoles(rolesWithNew);
        setLastLoadedRoles(rolesWithNew);
      }
      handleCloseRoleDialog();
      toast.success(langIndex === 2 ? 'Сохранено' : langIndex === 3 ? 'Պահպանվեց' : 'Saved');
      navigate(`/short_function/employees/permissions/role/${roleKey}?edit=1`);
    } catch (e) {
      console.error(e);
      toast.error(langIndex === 2 ? 'Не удалось сохранить' : langIndex === 3 ? 'Չհաջողվեց պահպանել' : 'Save failed');
    } finally {
      setSavingNewRole(false);
    }
  };

  const handleDeleteRoleLocal = async (roleKey) => {
    const role = roles[roleKey];
    if (!role) return;

    if (role.isDefault) {
      toast.error(
        langIndex === 1 ? 'Cannot delete default role' :
        langIndex === 2 ? 'Нельзя удалить роль по умолчанию' :
        'Հնարավոր չէ ջնջել լռելյայն դերը'
      );
      return;
    }

    if (!window.confirm(langIndex === 2 ? 'Удалить роль?' : langIndex === 3 ? 'Ջնջե՞լ դերը' : 'Delete role?')) {
      return;
    }

    setDeletingRoleKey(roleKey);
    try {
      const idToSend = role.dbId ?? roleKey;
      await Api.rolesDelete(idToSend);
      setRoles((prev) => {
        const next = { ...prev };
        delete next[roleKey];
        return next;
      });
      setLastLoadedRoles((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next[roleKey];
        return next;
      });
      toast.success(langIndex === 2 ? 'Роль удалена' : langIndex === 3 ? 'Դերը ջնջվեց' : 'Role deleted');
    } catch (e) {
      console.error(e);
      toast.error(langIndex === 2 ? 'Не удалось удалить' : langIndex === 3 ? 'Չհաջողվեց ջնջել' : 'Delete failed');
    } finally {
      setDeletingRoleKey(null);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        roles: Object.values(roles).map((r) => ({
          role_key: r.id,
          name_en: r.name?.[1] || '',
          name_ru: r.name?.[2] || '',
          name_hy: r.name?.[3] || '',
          color: r.color || null,
          is_default: r.isDefault ? 1 : 0,
          permissions: packPermissions(r.permissions, r.groupHistoryIds),
        })),
      };

      const resp = await Api.rolesSaveAll(payload);
      const list = resp?.data?.data;

      if (Array.isArray(list)) {
        const uiRoles = apiRolesToUiRoles(list);
        setRoles(uiRoles);
        setLastLoadedRoles(uiRoles);
      } else {
        setLastLoadedRoles(roles);
      }

      toast.success(langIndex === 2 ? 'Сохранено' : langIndex === 3 ? 'Պահպանվեց' : 'Saved');
      setIsEditMode(false);
    } catch (e) {
      console.error(e);
      toast.error(langIndex === 2 ? 'Не удалось сохранить' : langIndex === 3 ? 'Չհաջողվեց պահպանել' : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setRoles(lastLoadedRoles || defaultRoles);
  };

  if (loadingRoles) {
    return (
      <Wrapper>
        <Box sx={eps.loadingBox}>
          <CircularProgress sx={eps.loadingProgress(color)} />
          <Typography>{langIndex === 2 ? 'Загрузка...' : langIndex === 3 ? 'Բեռնվում է...' : 'Loading...'}</Typography>
        </Box>
      </Wrapper>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {(shortFunction.employee_permissions?.[langIndex] || 'Employee Permissions')} - {menu.worker?.[langIndex] || ''}
        </title>
      </Helmet>

      <Wrapper>
        <div className="tableParent">
          <Paper sx={eps.paper}>
            <Box sx={{ ...eps.headerBox, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5 }}>
              <Box sx={eps.headerLeftBox}>
                <Button onClick={() => navigate('/short_function')} sx={{ ...eps.backButton, color, '&:hover': { backgroundColor: `${color}15` } }}>
                  <ArrowBackIosNewIcon />
                </Button>

                <Typography variant="h5" component="h1" sx={eps.titleTypography}>
                  {shortFunction.employee_permissions?.[langIndex] || 'Employee Permissions'}
                </Typography>
              </Box>

              <Box sx={eps.headerRightBox}>
                {!isEditMode ? (
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditMode(true)}
                    sx={eps.editButton(color)}
                  >
                    {langIndex === 2 ? 'Редактировать' : langIndex === 3 ? 'Խմբագրել' : 'Edit'}
                  </Button>
                ) : (
                  <>
                   
                   
                  </>
                )}
              </Box>
            </Box>

            <Alert
              icon={<InfoIcon />}
              severity="success"
              sx={
                REACT_APP_SERVER !== 'ATS'
                  ? { ...eps.alertBox }
                  : {
                    ...eps.alertBox,
                    border: `1px solid ${color}`,
                    backgroundColor: `${color}10`,
                    '& .MuiAlert-icon': { color },
                  }
              }
            >
              <Typography variant="body2">
                {langIndex === 2
                  ? 'Роли определяют, что могут делать сотрудники. Нажмите «Просмотр» на карточке роли, чтобы увидеть и изменить её разрешения. Кнопка «Редактировать» выше позволяет добавлять роли и сохранять изменения в базу.'
                  : langIndex === 3
                    ? 'Դերերը որոշում են, թե ինչ կարող են աշխատակիցները։ Սեղմեք «Դիտել» դերի քարտի վրա՝ թույլտվությունները դիտելու և փոխելու համար։ Վերևի «Խմբագրել» կոճակը թույլ է տալիս ավելացնել դերեր և պահպանել փոփոխությունները։'
                    : 'Roles define what employees can do. Click "View" on a role card to see and edit its permissions. Use "Edit" above to add roles and save changes to the database.'}
              </Typography>
            </Alert>

            <Grid container spacing={2} sx={eps.gridContainer}>
              {rolesArray.map((role) => (
                <Grid item xs={12} sm={4} key={role.id} sx={eps.gridItem}>
                  <Card sx={eps.roleCard(role.color, color)}>
                    <CardContent sx={eps.cardContent}>
                      <Box sx={eps.chipContainerBox}>
                        <Chip
                          label={role.name?.[langIndex] || role.id}
                          sx={eps.chip(role.color)}
                        />
                      </Box>
                    </CardContent>
                    <Box sx={eps.viewButtonBox} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={langIndex === 2 ? 'Просмотр' : langIndex === 3 ? 'Դիտել' : 'View'}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/short_function/employees/permissions/role/${role.id}`)}
                          sx={eps.viewIconButton(color)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isEditMode && (
                        <>
                          <Tooltip title={langIndex === 2 ? 'Редактировать роль' : langIndex === 3 ? 'Խմբագրել դերը' : 'Edit role'}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/short_function/employees/permissions/role/${role.id}?edit=1`)}
                              sx={eps.editIconButton(role.color)}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </IconButton>
                          </Tooltip>
                          {!role.isDefault && (
                            <Tooltip title={langIndex === 2 ? 'Удалить роль' : langIndex === 3 ? 'Ջնջել դերը' : 'Delete role'}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRoleLocal(role.id)}
                                disabled={deletingRoleKey === role.id}
                                sx={eps.deleteIconButton}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </Card>
                </Grid>
              ))}
              {isEditMode && (
                <Grid item xs={12} sm={4} sx={eps.gridItem}>
                  <Card
                    sx={eps.addRoleCard(color)}
                    onClick={() => handleOpenRoleDialog()}
                  >
                    <CardContent sx={eps.addRoleCardContent}>
                      <Box sx={eps.addRoleBox}>
                        <AddIcon sx={eps.addIcon} />
                        <Typography variant="body2" color="text.secondary">
                          {langIndex === 2 ? 'Добавить роль' : langIndex === 3 ? 'Ավելացնել դեր' : 'Add Role'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <Dialog open={openRoleDialog} onClose={handleCloseRoleDialog} maxWidth="md" fullWidth>
              <DialogTitle>
                {editingRoleKey
                  ? (langIndex === 2 ? 'Редактировать роль' : langIndex === 3 ? 'Խմբագրել դերը' : 'Edit Role')
                  : (langIndex === 2 ? 'Создать роль' : langIndex === 3 ? 'Ստեղծել դեր' : 'Create Role')}
              </DialogTitle>

              <DialogContent>
                <Box sx={eps.dialogContentBox}>
                  <TextField
                    label={langIndex === 2 ? 'Название (англ.)' : langIndex === 3 ? 'Անուն (անգլերեն)' : 'Name (English)'}
                    value={newRoleName.en}
                    onChange={(e) => setNewRoleName((p) => ({ ...p, en: e.target.value }))}
                    fullWidth
                    required
                  />
                  <TextField
                    label={langIndex === 2 ? 'Название (рус.)' : langIndex === 3 ? 'Անուն (ռուսերեն)' : 'Name (Russian)'}
                    value={newRoleName.ru}
                    onChange={(e) => setNewRoleName((p) => ({ ...p, ru: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label={langIndex === 2 ? 'Название (арм.)' : langIndex === 3 ? 'Անուն (հայերեն)' : 'Name (Armenian)'}
                    value={newRoleName.hy}
                    onChange={(e) => setNewRoleName((p) => ({ ...p, hy: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label={langIndex === 2 ? 'Цвет' : langIndex === 3 ? 'Գույն' : 'Color'}
                    type="color"
                    value={newRoleColor}
                    onChange={(e) => setNewRoleColor(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleCloseRoleDialog} disabled={savingNewRole}>
                  {langIndex === 2 ? 'Отмена' : langIndex === 3 ? 'Չեղարկել' : 'Cancel'}
                </Button>
                <Button
                  onClick={handleSaveRoleLocal}
                  variant="contained"
                  disabled={savingNewRole}
                  sx={{ ...eps.dialogSaveButton(color), '&:hover': { backgroundColor: hoverColor } }}
                >
                  {savingNewRole
                    ? (langIndex === 2 ? 'Сохранение...' : langIndex === 3 ? 'Պահպանվում է...' : 'Saving...')
                    : (langIndex === 2 ? 'Далее' : langIndex === 3 ? 'Հաջորդ' : 'Next')}
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        </div>
      </Wrapper>
    </>
  );
}

export default EmployeePermissions;
